import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  styled,
  Chip,
  useMediaQuery,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Calendar,
  Star,
  Package as PackageIcon,
  Clock as ClockIcon,
  Zap,
  TrendingDown,
  Crown,
  RefreshCw,
  Shuffle
} from 'lucide-react';
import { motion } from 'framer-motion';
import './SubscriptionAndShipping.css';

// Enhanced styled components with updated design system
const Title = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#111827',
  textAlign: 'center',
  marginBottom: '32px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    backgroundImage: 'linear-gradient(to right, #ff385c, #ff5c83)',
    borderRadius: '2px',
  }
}));

const OptionsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '24px',
  marginBottom: '40px',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  }
}));

const OptionCard = styled(motion.div)(({ theme, selected }) => ({
  padding: '28px',
  borderRadius: '16px',
  border: '2px solid',
  borderColor: selected ? '#ff385c' : '#e5e7eb',
  background: selected 
    ? 'linear-gradient(145deg, #fff5f7 0%, #fff1f2 100%)' 
    : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: selected 
    ? '0 10px 25px -5px rgba(255, 56, 92, 0.1), 0 8px 10px -6px rgba(255, 56, 92, 0.05)'
    : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  transform: selected ? 'scale(1.01)' : 'scale(1)',
}));

const CardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  position: 'relative',
  zIndex: 1
});

const CardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px',
  width: '100%'
});

const IconWrapper = styled(Box)(({ theme, selected }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: selected ? '#FFE4E8' : '#F9FAFB',
  color: '#ff385c',
  boxShadow: selected ? '0 8px 16px -4px rgba(255, 56, 92, 0.2)' : '0 4px 6px rgba(0, 0, 0, 0.05)',
  position: 'relative'
}));

const RecommendedChip = styled(Chip)({
  backgroundColor: '#ff385c',
  color: 'white',
  fontWeight: 600,
  fontSize: '14px',
  height: '32px',
  padding: '0 16px',
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(255, 56, 92, 0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
});

const SocialProofText = styled(Typography)({
  fontSize: '13px',
  color: '#6B7280',
  textAlign: 'center',
  marginTop: '8px',
  fontStyle: 'italic'
});

const PlanTitle = styled(Typography)({
  fontSize: '28px',
  fontWeight: 700,
  color: '#111827',
  marginBottom: '8px',
  textAlign: 'center'
});

const SubTitle = styled(Typography)({
  fontSize: '16px',
  color: '#4B5563',
  marginBottom: '24px',
  textAlign: 'center',
  lineHeight: 1.5
});

const PriceWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  marginBottom: '24px'
});

const PriceAmount = styled(Typography)({
  fontSize: '48px',
  fontWeight: 800,
  color: '#ff385c',
  lineHeight: 1,
  letterSpacing: '-0.02em',
  fontFeatureSettings: '"tnum"',
  fontVariantNumeric: 'tabular-nums'
});

const PriceUnit = styled(Typography)({
  fontSize: '20px',
  fontWeight: 500,
  color: '#6B7280',
  marginLeft: '4px'
});

const ChipsContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginBottom: '24px',
  justifyContent: 'center',
  flexWrap: 'wrap'
});

const StyledChip = styled(Chip)(({ variant }) => ({
  height: '28px',
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: '14px',
  padding: '0 12px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  ...(variant === 'save' && {
    backgroundColor: '#DCFCE7',
    color: '#059669'
  }),
  ...(variant === 'popular' && {
    backgroundColor: '#FFE4E8',
    color: '#ff385c'
  })
}));

const BenefitsList = styled('ul')({
  listStyle: 'none',
  padding: '0 0 0 4px',
  margin: '8px 0 16px',
  color: '#4B5563',
  textAlign: 'left'
});

const BenefitItem = styled(motion.li)({
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  fontSize: '15px',
  lineHeight: 1.5,
  textAlign: 'left'
});

const BenefitText = styled('span')({
  textAlign: 'left'
});

const QuarterlyList = styled('ul')({
  listStyle: 'none',
  padding: '0 0 0 8px',
  margin: '0 0 24px',
  width: '100%',
  textAlign: 'left'
});

const QuarterlyItem = styled('li')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
  color: '#4B5563',
  fontSize: '15px',
  lineHeight: 1.5,
  textAlign: 'left',
  '&:last-child': {
    marginBottom: 0
  }
});

const BulletPoint = styled(Box)(({ theme }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: '#6B7280',
  marginRight: '12px',
  flexShrink: 0
}));

const BillingBox = styled(Box)(({ theme, selected }) => ({
  backgroundColor: 'rgba(255, 56, 92, 0.05)',
  padding: '16px',
  borderRadius: '8px',
  marginTop: '24px',
  textAlign: 'center',
  fontWeight: selected ? 600 : 500,
  color: '#111827'
}));

const ShippingPrice = styled(Typography)({
  fontSize: '24px',
  fontWeight: 700,
  marginTop: 'auto',
  textAlign: 'center',
  color: '#111827'
});

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const benefitItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { 
      type: "spring",
      stiffness: 500,
      damping: 30
    }
  }
};

const SubscriptionAndShipping = ({
  subscriptionType,
  setSubscriptionType,
  shippingMethod,
  setShippingMethod,
  isEsim
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const annualBenefits = [
    {
      text: 'Save $48 annually (33% off monthly price)',
      icon: <TrendingDown size={18} color="#059669" />
    },
    {
      text: 'Premium support with dedicated response times',
      icon: <Crown size={18} color="#059669" />
    },
    {
      text: 'Flexibility to change plans at no additional cost',
      icon: <RefreshCw size={18} color="#059669" />
    },
    {
      text: 'Seamless carrier switching without penalties',
      icon: <Shuffle size={18} color="#059669" />
    }
  ];

  const quarterlyUses = [
    'Temporary phone lines',
    'Trying out a new carrier/network',
    'Seasonal use',
    'Travel'
  ];

  return (
    <motion.div
      className="subscription-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Box sx={{ maxWidth: 800, margin: '0 auto', padding: '0 24px' }}>
        <Title variant="h5">Choose Your Subscription</Title>
        
        <OptionsContainer>
          <OptionCard
            variants={cardVariants}
            selected={subscriptionType === 'annual'}
            onClick={() => setSubscriptionType('annual')}
            whileHover={{ 
              y: -4,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            className="subscription-card"
          >
            <CardContent>
              <CardHeader>
                <IconWrapper selected={subscriptionType === 'annual'}>
                  <Calendar size={30} color="#ff385c" />
                </IconWrapper>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <RecommendedChip 
                    label="Recommended" 
                    className="recommended-chip"
                  />
                  <SocialProofText>
                    85% of our users choose Annual
                  </SocialProofText>
                </Box>
              </CardHeader>

              <PlanTitle>Annual Plan</PlanTitle>
              <SubTitle>Save more with an annual commitment</SubTitle>

              <PriceWrapper>
                <PriceAmount className="price-amount">$8</PriceAmount>
                <PriceUnit>/mo</PriceUnit>
              </PriceWrapper>

              <ChipsContainer>
                <StyledChip 
                  icon={<Zap size={14} />}
                  label="Save 33%" 
                  variant="save" 
                />
                <StyledChip
                  icon={<Star size={14} />}
                  label="Most Popular"
                  variant="popular"
                />
              </ChipsContainer>

              <BenefitsList>
                {annualBenefits.map((benefit, index) => (
                  <BenefitItem 
                    key={index}
                    variants={benefitItemVariants}
                    custom={index}
                    className="benefit-item"
                  >
                    {benefit.icon}
                    <BenefitText>{benefit.text}</BenefitText>
                  </BenefitItem>
                ))}
              </BenefitsList>
              
              <BillingBox selected={subscriptionType === 'annual'}>
                One payment of $96 today covers your entire year
              </BillingBox>
            </CardContent>
          </OptionCard>

          <OptionCard
            variants={cardVariants}
            selected={subscriptionType === '3months'}
            onClick={() => setSubscriptionType('3months')}
            whileHover={{ 
              y: -4,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            className="subscription-card"
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <IconWrapper selected={subscriptionType === '3months'}>
                  <Calendar size={30} color="#ff385c" />
                </IconWrapper>
              </Box>

              <PlanTitle>3 Months Plan</PlanTitle>
              <SubTitle>Perfect for short-term needs without long commitments</SubTitle>

              <PriceWrapper>
                <PriceAmount className="price-amount">$12</PriceAmount>
                <PriceUnit>/mo</PriceUnit>
              </PriceWrapper>

              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#4B5563',
                  textAlign: 'left',
                  marginBottom: '16px',
                  paddingLeft: '8px'
                }}
              >
                Great for:
              </Typography>

              <QuarterlyList>
                {quarterlyUses.map((use, index) => (
                  <QuarterlyItem key={index}>
                    <BulletPoint />
                    {use}
                  </QuarterlyItem>
                ))}
              </QuarterlyList>

              <BillingBox selected={subscriptionType === '3months'}>
                $36 billed today, renews quarterly
              </BillingBox>
            </CardContent>
          </OptionCard>
        </OptionsContainer>

        {!isEsim && (
          <>
            <Title variant="h5">Choose Your Shipping</Title>
            <OptionsContainer>
              <OptionCard
                variants={cardVariants}
                selected={shippingMethod === 'standard'}
                onClick={() => setShippingMethod('standard')}
                whileHover={{ 
                  y: -4,
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
                className="shipping-card"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                    <IconWrapper selected={shippingMethod === 'standard'}>
                      <PackageIcon size={30} color="#ff385c" />
                    </IconWrapper>
                  </Box>
                  <PlanTitle>Standard Shipping</PlanTitle>
                  <SubTitle>Reliable delivery within 5-7 business days</SubTitle>
                  <ShippingPrice>
                    $8.00
                  </ShippingPrice>
                </CardContent>
              </OptionCard>

              <OptionCard
                variants={cardVariants}
                selected={shippingMethod === 'expedited'}
                onClick={() => setShippingMethod('expedited')}
                whileHover={{ 
                  y: -4,
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }}
                className="shipping-card"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                    <IconWrapper selected={shippingMethod === 'expedited'}>
                      <ClockIcon size={30} color="#ff385c" />
                    </IconWrapper>
                  </Box>
                  <PlanTitle>Expedited Shipping</PlanTitle>
                  <SubTitle>Fast delivery within 2-3 business days</SubTitle>
                  <ShippingPrice>
                    $15.00
                  </ShippingPrice>
                </CardContent>
              </OptionCard>
            </OptionsContainer>
          </>
        )}
      </Box>
    </motion.div>
  );
};

SubscriptionAndShipping.propTypes = {
  subscriptionType: PropTypes.oneOf(['annual', '3months']).isRequired,
  setSubscriptionType: PropTypes.func.isRequired,
  shippingMethod: PropTypes.oneOf(['standard', 'expedited']).isRequired,
  setShippingMethod: PropTypes.func.isRequired,
  isEsim: PropTypes.bool.isRequired
};

export default SubscriptionAndShipping;